import { use, useEffect, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import { CLIENT_SECRET_PLACEHOLDER } from 'features/connectors/domain/client-secret-placeholder';
import { Connector, ConnectorType } from 'features/connectors/domain/connector';
import { CreateConnectorCommand } from 'features/connectors/domain/create-connector-command';
import { useCreateConnector, useDeleteConnector, useUpdateConnector } from 'features/connectors/domain/queries/connector-queries';
import OrganisationUnit from 'features/organisation/domain/models/organisation-unit';

export interface ConnectorDetailsHook {
  viewingMode: ViewingMode;
  mutationIsLoading: boolean;

  currentSelectedConnector: Connector | undefined;
  changeCurrentSelectedConnector: (connector: Connector | undefined) => void;

  editConnector: (connector: Connector) => void;
  addConnector: (fieldValues: FieldValues) => void;
  deleteConnector: (connector: Connector) => void;

  cancelMutation: () => void;

  isDetailsOpen: boolean;
  openDetailsInViewingMode: (connector: Connector) => void;
  openDetailsInEditingMode: (connector: Connector) => void;
  openDetailsInCreationMode: () => void;
  closeDetails: (isFormStateDirty: boolean) => void;

  form: UseFormReturn<Partial<Connector>, any>;

  isConnectorTypeValid: boolean;
  setIsConnectorTypeValid: (value: boolean) => void;

  isLocationPickerOpen: boolean;
  openLocationPicker: () => void;
  closeLocationPicker: () => void;
  updateSelectedLocations: (newLocations: OrganisationUnit[]) => void;

  isDeleteConfirmationPopupOpen: boolean;
  closeDeleteConfirmationPopup: () => void;
  deleteConfirmationAction: () => void;

  isCannotDeletePopupOpen: boolean;
  closeCannotDeletePopup: () => void;
}

const useConnectorDetails = (refresh: (connectorId?: string) => void): ConnectorDetailsHook => {
  const { showDefaultConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {} as Partial<Connector>,
  });

  const [mutationIsLoading, setMutationIsLoading] = useState<boolean>(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [viewingMode, setViewingMode] = useState<ViewingMode>('viewing');
  const [currentSelectedConnector, setCurrentSelectedConnector] = useState<Connector>();
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isConnectorTypeValid, setIsConnectorTypeValid] = useState(false);
  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState<boolean>(false);
  const [deleteConfirmationAction, setDeleteConfirmationAction] = useState(() => () => {});
  const [isCannotDeletePopupOpen, setIsCannotDeletePopupOpen] = useState<boolean>(false);

  const {
    mutate: updateConnector,
    isPending: updateConnectorIsLoading,
    isSuccess: updateConnectorIsSuccess,
    isError: updateConnectorIsError,
    error: updateConnectorError,
    data: updateConnectorData,
  } = useUpdateConnector();

  const {
    mutate: createConnector,
    isPending: createConnectorIsLoading,
    isSuccess: createConnectorIsSuccess,
    isError: createConnectorIsError,
    error: createConnectorError,
    data: createConnectorData,
  } = useCreateConnector();

  const {
    mutate: deleteConnectorMutation,
    isPending: deleteConnectorIsPending,
    isSuccess: deleteConnectorIsSuccess,
    isError: deleteConnectorIsError,
    error: deleteConnectorError,
  } = useDeleteConnector();

  const setFormValuesWithCurrentConnector = () => {
    form.reset({
      name: currentSelectedConnector?.name ?? '',
      note: currentSelectedConnector?.note ?? '',
      type: currentSelectedConnector?.type ?? ConnectorType.Unknown,
      organisationUnits: currentSelectedConnector?.organisationUnits ?? [],
      vcareConnectorData:
        currentSelectedConnector?.type === ConnectorType.Vcare
          ? {
              organisationId: currentSelectedConnector.vcareConnectorData?.organisationId ?? '',
              clientId: currentSelectedConnector.vcareConnectorData?.clientId ?? '',
              clientSecret: CLIENT_SECRET_PLACEHOLDER,
            }
          : undefined,
    });
  };

  useEffect(() => {
    setFormValuesWithCurrentConnector();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedConnector]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
  }, [form.formState.isDirty]);

  useEffect(() => {
    if (updateConnectorIsSuccess && updateConnectorData) {
      setViewingMode('viewing');
      refresh(updateConnectorData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateConnectorIsSuccess]);

  useEffect(() => {
    if (createConnectorIsSuccess && createConnectorData) {
      setViewingMode('viewing');
      refresh(createConnectorData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createConnectorIsSuccess]);

  useEffect(() => {
    if (updateConnectorIsError && updateConnectorError) {
      setErrorMessage(updateConnectorError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateConnectorIsError]);

  useEffect(() => {
    if (createConnectorIsError && createConnectorError) {
      setErrorMessage(createConnectorError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createConnectorIsError]);

  useEffect(() => {
    if (deleteConnectorIsSuccess) {
      setIsDeleteConfirmationPopupOpen(false);
      setIsDetailsOpen(false);
      refresh();
    }
  }, [deleteConnectorIsSuccess]);

  useEffect(() => {
    if (deleteConnectorIsError && deleteConnectorError) {
      setErrorMessage(deleteConnectorError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteConnectorIsError]);

  useEffect(() => {
    setMutationIsLoading(updateConnectorIsLoading || createConnectorIsLoading || deleteConnectorIsPending);
  }, [updateConnectorIsLoading, createConnectorIsLoading, deleteConnectorIsPending]);

  const changeCurrentSelectedConnector = (connector: Connector | undefined) => {
    setCurrentSelectedConnector(connector);
  };

  const addConnector = (fieldValues: FieldValues) => {
    const connector: CreateConnectorCommand = {
      name: fieldValues.name ?? '',
      type: fieldValues.type ?? ConnectorType.Unknown,
      note: fieldValues.note?.trim(),
      vcareConnectorData: {
        clientId: fieldValues.vcareConnectorData.clientId ?? '',
        organisationId: fieldValues.vcareConnectorData.organisationId ?? '',
        clientSecret: fieldValues.vcareConnectorData.clientSecret ?? '',
      },
    };
    createConnector(connector);
  };

  const editConnector = (connector: Connector) => {
    updateConnector(connector);
  };

  const deleteConnector = (connector: Connector) => {
    setCurrentSelectedConnector(connector);
    if (connector.cannotDeleteReason) {
      setIsCannotDeletePopupOpen(true);
    } else {
      setIsDeleteConfirmationPopupOpen(true);
      setDeleteConfirmationAction(() => () => confirmDeleteConnector(connector));
    }
  };

  const confirmDeleteConnector = (connector: Connector) => {
    if (connector.id) {
      deleteConnectorMutation(connector.id);
    }
    setDeleteConfirmationAction(() => () => {});
    setCurrentSelectedConnector(undefined);
  };

  const openDetailsInCreationMode = () => {
    setCurrentSelectedConnector(undefined);
    setIsDetailsOpen(true);
    setViewingMode('creation');
    setIsConnectorTypeValid(false);
  };

  const openDetailsInEditingMode = (connector: Connector | undefined) => {
    setCurrentSelectedConnector(connector);
    setIsDetailsOpen(true);
    setViewingMode('editing');
    setFormValuesWithCurrentConnector();
  };

  const openDetailsInViewingMode = (connector: Connector | undefined) => {
    setCurrentSelectedConnector(connector);
    setIsDetailsOpen(true);
    setViewingMode('viewing');
  };

  const closeDetails = (isFormStateDirty: boolean) => {
    showDefaultConfirmationPopUp({
      shouldShow: (viewingMode === 'creation' || viewingMode === 'editing') && isFormStateDirty,
      action: () => {
        setIsDetailsOpen(false);
        setCurrentSelectedConnector(undefined);
        setIsConnectorTypeValid(false);
        resetForm();
      },
    });
  };

  const cancelMutation = () => {
    if (viewingMode === 'editing') {
      setViewingMode('viewing');
    }
    setFormValuesWithCurrentConnector();
  };

  const resetForm = () => {
    form.reset({
      name: '',
      note: '',
      type: ConnectorType.Unknown,
      organisationUnits: [],
    });
  };

  const openLocationPicker = (): void => {
    setIsLocationPickerOpen(true);
  };

  const closeLocationPicker = (): void => {
    setIsLocationPickerOpen(false);
  };

  const closeDeleteConfirmationPopup = () => {
    setIsDeleteConfirmationPopupOpen(false);
  };

  const closeCannotDeletePopup = () => {
    setIsCannotDeletePopupOpen(false);
  };

  const sortLocations = (locations: OrganisationUnit[]): OrganisationUnit[] => {
    return locations.sort((locationA, locationB) => locationA.name.localeCompare(locationB.name));
  };

  const updateSelectedLocations = (newLocations: OrganisationUnit[]): void => {
    form.setValue('organisationUnits', sortLocations(newLocations), {
      shouldDirty: true,
    });
  };

  return {
    viewingMode,
    mutationIsLoading,

    currentSelectedConnector,
    changeCurrentSelectedConnector,

    editConnector,
    addConnector,
    deleteConnector,

    cancelMutation,

    isDetailsOpen,
    openDetailsInCreationMode,
    openDetailsInEditingMode,
    openDetailsInViewingMode,
    closeDetails,

    form,

    isConnectorTypeValid,
    setIsConnectorTypeValid,

    isLocationPickerOpen,
    openLocationPicker,
    closeLocationPicker,
    updateSelectedLocations,

    isDeleteConfirmationPopupOpen,
    closeDeleteConfirmationPopup,
    deleteConfirmationAction,

    isCannotDeletePopupOpen,
    closeCannotDeletePopup,
  };
};

export default useConnectorDetails;
